import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { PeriodFilter, PeriodRange, defaultPeriod } from '@/components/ui/PeriodFilter';

function formatLakh(n: number): string {
  if (!n || isNaN(n)) return '₹0';
  return '₹' + (n / 100000).toFixed(2) + 'L';
}

function getTodayStr(): string {
  const now = new Date();
  const pad = (x: number) => String(x).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export const FinancePulseWidget: React.FC = () => {
  const navigate = useNavigate();
  const [period, setPeriod] = useState<PeriodRange>(defaultPeriod());

  const { data: pulseResponse, isLoading, isError } = useQuery({
    queryKey: ['finance-pulse', period],
    queryFn: () => api.getFinancePulse({ dateFrom: period.dateFrom, dateTo: period.dateTo }),
    staleTime: 5 * 60 * 1000,
  });

  const data = pulseResponse?.data || {};
  const today = getTodayStr();

  const cards = [
    {
      label: 'Sales Booked',
      value: isLoading ? null : (data.salesBooked != null ? formatLakh(data.salesBooked) : '—'),
      subtext: isLoading ? null : (data.salesBookedCount != null ? `${data.salesBookedCount} deals` : null),
      colorClass: 'bg-green-50 border-green-200',
      iconBg: 'bg-green-100',
      textColor: 'text-green-700',
      subtextColor: 'text-green-600',
      onClick: () => navigate(`/finance?dateFrom=${period.dateFrom || ''}&dateTo=${period.dateTo || ''}&tab=all`),
    },
    {
      label: 'Cash In',
      value: isLoading ? null : (data.cashIn != null ? formatLakh(data.cashIn) : '—'),
      subtext: isLoading ? null : (data.cashInCount != null ? `${data.cashInCount} payments` : null),
      colorClass: 'bg-teal-50 border-teal-200',
      iconBg: 'bg-teal-100',
      textColor: 'text-teal-700',
      subtextColor: 'text-teal-600',
      onClick: () => navigate(`/finance?dateFrom=${period.dateFrom || ''}&dateTo=${period.dateTo || ''}&tab=all`),
    },
    {
      label: 'Pending',
      value: isLoading ? null : (data.pending != null ? formatLakh(data.pending) : '—'),
      subtext: null,
      colorClass: 'bg-orange-50 border-orange-200',
      iconBg: 'bg-orange-100',
      textColor: 'text-orange-700',
      subtextColor: 'text-orange-600',
      onClick: () => navigate('/finance?tab=upcoming'),
    },
    {
      label: 'Overdue Milestones',
      value: isLoading ? null : (data.overdueMilestones != null ? String(data.overdueMilestones) : '—'),
      subtext: isLoading ? null : (data.overdueMilestonesAmount != null ? formatLakh(data.overdueMilestonesAmount) : null),
      colorClass: 'bg-red-50 border-red-200',
      iconBg: 'bg-red-100',
      textColor: 'text-red-700',
      subtextColor: 'text-red-600',
      onClick: () => navigate('/finance?tab=overdue'),
    },
    {
      label: "Today's Collection",
      value: isLoading ? null : (data.todayCollection != null ? formatLakh(data.todayCollection) : '—'),
      subtext: isLoading ? null : (data.todayCollectionCount != null ? `${data.todayCollectionCount} payment${data.todayCollectionCount !== 1 ? 's' : ''}` : null),
      colorClass: 'bg-amber-50 border-amber-200',
      iconBg: 'bg-amber-100',
      textColor: 'text-amber-700',
      subtextColor: 'text-amber-600',
      onClick: () => navigate(`/finance?dateFrom=${today}&dateTo=${today}&tab=all`),
    },
  ];

  return (
    <div className="space-y-3">
      {/* Section heading + period filter */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Finance Pulse</p>
        <PeriodFilter
          value={period}
          onChange={setPeriod}
          allowedPresets={['this_month', 'last_month', 'custom']}
          className="items-start sm:items-end"
        />
      </div>

      {isError && (
        <p className="text-xs text-red-500">Failed to load finance pulse data.</p>
      )}

      {/* Metric cards grid */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {cards.map((card) => (
          <div
            key={card.label}
            onClick={card.onClick}
            className={`rounded-lg border p-4 cursor-pointer hover:shadow-md transition-shadow ${card.colorClass}`}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); card.onClick(); } }}
            aria-label={`${card.label}: ${card.value ?? 'loading'} — click to view details`}
          >
            {isLoading ? (
              <div className="space-y-2">
                <div className="bg-gray-200 animate-pulse rounded h-4 w-20" />
                <div className="bg-gray-200 animate-pulse rounded h-6 w-16" />
                <div className="bg-gray-200 animate-pulse rounded h-4 w-12" />
              </div>
            ) : (
              <>
                <p className={`text-xs font-medium mb-1 ${card.subtextColor}`}>{card.label}</p>
                <p className={`text-xl font-bold ${card.textColor}`}>{card.value ?? '—'}</p>
                {card.subtext && (
                  <p className={`text-xs mt-0.5 ${card.subtextColor}`}>{card.subtext}</p>
                )}
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
